import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { FaArrowLeft } from 'react-icons/fa';
import { Button, FileInput } from '../../../components/ui';
import apiService from '../../../services/apiService';

const breadcrumb = (
  <span>
    Home &nbsp;/&nbsp; <a href="/web-pages" className="text-blue-600">Webpages</a> &nbsp;/&nbsp; <span className="text-gray-700">Add Webpage</span>
  </span>
);

const AddWebPage = () => {
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, formState: { errors, isSubmitting } } = useForm({
    defaultValues: {
      title: '',
      website: '',
      uri: '',
      description: '',
      metaTags: {
        keywords: '',
        ogImage: '',
        ogTitle: '',
        ogDescription: ''
      },
      viewFile: 'default.ejs',
      featureImg: '',
      status: true,
      scripts: ''
    }
  });

  const [loading, setLoading] = useState(false);
  const [websites, setWebsites] = useState([]);
  const [showCustomParams, setShowCustomParams] = useState(false);
  const [customParams, setCustomParams] = useState({});
  const [customParamKey, setCustomParamKey] = useState('');
  const [customParamValue, setCustomParamValue] = useState('');
  const [editingParam, setEditingParam] = useState({ status: false, key: '' });
  const [activeTab, setActiveTab] = useState('basic');

  // Watch the title field to auto-generate URI
  const title = watch('title');

  useEffect(() => {
    // Auto-generate URI from title
    if (title) {
      const newTitle = title.replace(/\s{2,}/g, ' ');
      const uri = (`/${newTitle?.replace(/\s/g, '-')?.toLowerCase()}` || "").replace(/_+$/, '/');
      setValue('uri', uri, { shouldValidate: true });
    }
  }, [title, setValue]);

  useEffect(() => {
    // Fetch websites for dropdown
    const fetchWebsites = async () => {
      try {
        setLoading(true);
        const websiteData = await apiService.request('/i/ims/website?status=true&select=title');
        setWebsites(websiteData);
        
        // If there's only one website, select it by default
        if (websiteData?.length === 1) {
          setValue('website', websiteData[0].id);
        }
      } catch (err) {
        console.error('Error fetching websites:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchWebsites();
  }, [setValue]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      
      // Add custom params to the data if enabled
      if (showCustomParams && Object.keys(customParams).length > 0) {
        data.customParams = customParams;
      }
      
      await apiService.request('/i/ims/frontpage', {
        method: 'POST',
        body: JSON.stringify(data)
      });
      
      // Show success message and navigate to the list
      alert('Webpage added successfully');
      navigate('/web-pages');
    } catch (error) {
      console.error('Error adding webpage:', error);
      alert(`Error adding webpage: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const addCustomParam = () => {
    if (!customParamKey || !customParamValue) {
      alert('Key and value cannot be blank');
      return;
    }

    if (editingParam.status) {
      // Update existing param
      setCustomParams(prev => {
        const newParams = { ...prev };
        delete newParams[editingParam.key];
        newParams[customParamKey] = customParamValue;
        return newParams;
      });
      setEditingParam({ status: false, key: '' });
    } else {
      // Add new param
      setCustomParams(prev => ({
        ...prev,
        [customParamKey]: customParamValue
      }));
    }

    // Reset form fields
    setCustomParamKey('');
    setCustomParamValue('');
  };

  const editCustomParam = (key) => {
    setEditingParam({ status: true, key });
    setCustomParamKey(key);
    setCustomParamValue(customParams[key]);
  };

  const removeCustomParam = (key) => {
    setCustomParams(prev => {
      const newParams = { ...prev };
      delete newParams[key];
      return newParams;
    });
  };
  
  const updateCustomParam = () => {
    if (!customParamKey || !customParamValue) {
      alert('Key and value cannot be blank');
      return;
    }
    
    setCustomParams(prev => {
      const newParams = { ...prev };
      delete newParams[editingParam.key];
      newParams[customParamKey] = customParamValue;
      return newParams;
    });
    
    setEditingParam({ status: false, key: '' });
    setCustomParamKey('');
    setCustomParamValue('');
  };
  
  const cancelEditCustomParam = () => {
    setEditingParam({ status: false, key: '' });
    setCustomParamKey('');
    setCustomParamValue('');
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Add New Webpage</h1>
        <Button
          onClick={() => navigate('/web-pages')}
          variant="light"
          size="sm"
          icon={FaArrowLeft}
        >
          Back to List
        </Button>
      </div>

      <div className="flex items-center text-sm mb-6">
        <span className="text-gray-500">Home</span>
        <span className="mx-2">/</span>
        <span onClick={() => navigate('/web-pages')} className="text-gray-500">Webpages</span>
        <span className="mx-2">/</span>
        <span className="text-blue-600">New</span>
      </div>

      <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">        
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab('details')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'details'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('meta')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'meta'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meta Information
            </button>
            <button
              onClick={() => setActiveTab('scripts')}
              className={`px-6 py-3 text-sm font-medium ${
                activeTab === 'scripts'
                  ? 'border-b-2 border-blue-500 text-blue-600'
                  : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Meta Information
            </button>
            {showCustomParams && Object.keys(customParams).length > 0 && (
              <button
                onClick={() => setActiveTab('params')}
                className={`px-6 py-3 text-sm font-medium ${
                  activeTab === 'params'
                    ? 'border-b-2 border-blue-500 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Additional Details
              </button>
            )}
          </nav>
        </div>
        <div className="p-6">

          <form onSubmit={handleSubmit(onSubmit)}>
            {activeTab === 'basic' && (
              <div className="mb-8">
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="title">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  {...register('title', { required: 'Title is required' })}
                  placeholder="Enter page title"
                />
                {errors.title && (
                  <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="uri">
                  URI <span className="text-red-500">*</span>
                </label>
                <input
                  id="uri"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  {...register('uri', { required: 'URI is required' })}
                  placeholder="e.g., /about-us"
                />
                {errors.uri && (
                  <p className="text-red-500 text-sm mt-1">{errors.uri.message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="website">
                  Website <span className="text-red-500">*</span>
                </label>
                <select
                  id="website"
                  className="w-full border rounded px-3 py-2"
                  {...register('website', { required: 'Website is required' })}
                  disabled={websites.length === 0}
                >
                  <option value="">{websites.length === 0 ? 'Loading websites...' : 'Select Website'}</option>
                  {websites.map((website) => (
                    <option key={website.id} value={website.id}>
                      {website.title}
                    </option>
                  ))}
                </select>
                {errors.website && (
                  <p className="text-red-500 text-sm mt-1">{errors.website.message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="viewFile">
                  View File <span className="text-red-500">*</span>
                </label>
                <input
                  id="viewFile"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  {...register('viewFile', { required: 'View file is required' })}
                  placeholder="e.g., default.ejs"
                />
                {errors.viewFile && (
                  <p className="text-red-500 text-sm mt-1">{errors.viewFile.message}</p>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="description">
                  Description
                </label>
                <textarea
                  id="description"
                  className="w-full border rounded px-3 py-2"
                  rows="3"
                  {...register('description')}
                  placeholder="Enter page description"
                ></textarea>
              </div>

              <FileInput
                label="Feature Image"
                value={watch('featureImg')}
                onChange={(url) => setValue('featureImg', url)}
                allowedTypes={['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP']}
                showPreview={true}
                placeholder="Choose feature image"
              />

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="status">
                  Status <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center">
                  <input
                    id="statusActive"
                    type="radio"
                    value="true"
                    {...register('status', { required: 'Status is required' })}
                    defaultChecked
                  />
                  <label htmlFor="statusActive" className="ml-2 mr-4">Active</label>
                  
                  <input
                    id="statusInactive"
                    type="radio"
                    value="false"
                    {...register('status')}
                  />
                  <label htmlFor="statusInactive" className="ml-2">Inactive</label>
                </div>
                {errors.status && (
                  <p className="text-red-500 text-sm mt-1">{errors.status.message}</p>
                )}
              </div>
              </div>
            )}

            {activeTab === 'meta' && (
              <div className="mb-8">
              
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="metaTags.keywords">
                  Keywords
                </label>
                <input
                  id="metaTags.keywords"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  {...register('metaTags.keywords')}
                  placeholder="e.g., keyword1, keyword2, keyword3"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogTitle">
                  OG Title
                </label>
                <input
                  id="metaTags.ogTitle"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  {...register('metaTags.ogTitle')}
                  placeholder="e.g., My Awesome Page Title"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogDescription">
                  OG Description
                </label>
                <textarea
                  id="metaTags.ogDescription"
                  className="w-full border rounded px-3 py-2"
                  rows="2"
                  {...register('metaTags.ogDescription')}
                  placeholder="Enter meta description"
                ></textarea>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="metaTags.ogImage">
                  OG Image
                </label>
                <input
                  id="metaTags.ogImage"
                  type="text"
                  className="w-full border rounded px-3 py-2"
                  placeholder="Image URL"
                  {...register('metaTags.ogImage')}
                />
              </div>
              </div>
            )}

            {activeTab === 'params' && (
              <div className="mb-8">            
              <div className="mb-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="showCustomParams"
                    checked={showCustomParams}
                    onChange={() => setShowCustomParams(!showCustomParams)}
                    className="mr-2"
                  />
                  <label htmlFor="showCustomParams" className="text-gray-700">
                    Need Custom Params?
                  </label>
                </div>
              </div>

              {showCustomParams && (
                <div className="mb-4 p-4 border rounded">

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-gray-700 mb-2" htmlFor="customParamKey">
                        Parameter Key*
                      </label>
                      <input
                        type="text"
                        id="customParamKey"
                        className="w-full border rounded px-3 py-2"
                        value={customParamKey}
                        onChange={(e) => setCustomParamKey(e.target.value)}
                        placeholder="Enter parameter key"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-700 mb-2" htmlFor="customParamValue">
                        Parameter Value*
                      </label>
                      <input
                        type="text"
                        id="customParamValue"
                        className="w-full border rounded px-3 py-2"
                        value={customParamValue}
                        onChange={(e) => setCustomParamValue(e.target.value)}
                        placeholder="Enter parameter value"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end">
                    {editingParam.status ? (
                      <div>
                        <Button
                          type="button"
                          onClick={updateCustomParam}
                          variant="primary"
                          size="sm"
                          disabled={!customParamKey || !customParamValue}
                        >
                          Update
                        </Button>
                        <Button
                          type="button"
                          onClick={cancelEditCustomParam}
                          variant="ghost"
                          size="sm"
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        type="button"
                        onClick={addCustomParam}
                        variant="primary"
                        size="sm"
                        disabled={!customParamKey || !customParamValue}
                      >
                        Add Parameter
                      </Button>
                    )}
                  </div>

                  {Object.keys(customParams).length > 0 && (
                    <div className="mt-4">
                      <h3 className="font-semibold mb-2">Added Parameters:</h3>
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-white">
                          <thead>
                            <tr>
                              <th className="py-2 px-4 border-b text-left">Key</th>
                              <th className="py-2 px-4 border-b text-left">Value</th>
                              <th className="py-2 px-4 border-b text-left">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {Object.entries(customParams).map(([key, value], index) => (
                              <tr key={key}>
                                <td className="py-2 px-4 border-b">{key}</td>
                                <td className="py-2 px-4 border-b">{value}</td>
                                <td className="py-2 px-4 border-b">
                                  <Button
                                    type="button"
                                    onClick={() => editCustomParam(key)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-blue-600 hover:text-blue-800"
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    type="button"
                                    onClick={() => removeCustomParam(key)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    Remove
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
              </div>
            )}

            {activeTab === 'scripts' && (
              <div className="mb-8">
              <div className="mb-4">
                <label className="block text-gray-700 mb-2" htmlFor="scripts">
                  Custom Scripts
                </label>
                <textarea
                  id="scripts"
                  className="w-full border rounded px-3 py-2"
                  rows="4"
                  placeholder="Add custom JavaScript here"
                  {...register('scripts')}
                ></textarea>
              </div>
              </div>
            )}

            <div className="flex justify-between mt-6">
              <button
                type="button"
                onClick={() => navigate('/web-pages')}
                className="bg-gray-300 text-gray-800 px-6 py-2 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className={`bg-blue-600 text-white px-6 py-2 rounded ${(isSubmitting || loading) ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting || loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </div>
        
      </div>

      <div className="mt-6 bg-gray-50 border rounded-lg p-4">
        <h3 className="font-semibold mb-2">Instructions:</h3>
        <ul className="list-disc pl-5">
          <li>Fields marked with <span className="text-red-500">*</span> are mandatory.</li>
          <li>URI will be auto-generated from the title, but you can modify it.</li>
          <li>Custom parameters are optional and can be added as needed.</li>
        </ul>
      </div>
    </div>
  );
};

export default AddWebPage;
